/* ============================================================
   Reformly Board PDP — one Swiper initialiser for every carousel
   on the page.

   Declarative: a section marks its root [data-bp-swiper] and puts
   its options in data-bp-opts as JSON. No section ships its own
   init script, so adding a carousel is markup only.

   Swiper itself is the theme's existing swiper-bundle.min.js — 14
   other sections already load it, so it is warm in cache.
   ============================================================ */
(function () {
  'use strict';

  var PRESETS = {
    // "Just 20 Minutes A Day" — 4 video cards
    videos: {
      slidesPerView: 1.15,
      spaceBetween: 12,
      breakpoints: {
        750: { slidesPerView: 2.2, spaceBetween: 16 },
        990: { slidesPerView: 4, spaceBetween: 20 }
      }
    },
    // Written reviews rail
    reviews: {
      slidesPerView: 1.1,
      spaceBetween: 12,
      breakpoints: {
        750: { slidesPerView: 2.2, spaceBetween: 16 },
        990: { slidesPerView: 3, spaceBetween: 20 }
      }
    },
    // Before/after proof slides
    proof: {
      slidesPerView: 1.05,
      spaceBetween: 12,
      breakpoints: {
        750: { slidesPerView: 2, spaceBetween: 16 },
        990: { slidesPerView: 3, spaceBetween: 24 }
      }
    },
    /* Review strip. Matches the export's state machine exactly:
       per-view 1/2/3/4 at 900/1200/1500, 900ms cubic-bezier easing,
       loop, and a 5000ms autoplay that restarts on arrow use. */
    strip: {
      slidesPerView: 1,
      /* No gap: the design spaces slides with padding-right INSIDE each
         one (clamp(20px, 2.4vw, 40px)), so the slide box is a clean
         container/perView. spaceBetween would add on top and make every
         card wider than the design's 318.188px. */
      spaceBetween: 0,
      loop: true,
      speed: 900,
      autoplay: { delay: 5000, disableOnInteraction: false, pauseOnMouseEnter: true },
      breakpoints: {
        900: { slidesPerView: 2 },
        1200: { slidesPerView: 3 },
        1500: { slidesPerView: 4 }
      }
    },
    // Member testimonial videos, 4:5
    testimonials: {
      slidesPerView: 1.2,
      spaceBetween: 12,
      breakpoints: {
        750: { slidesPerView: 2.4, spaceBetween: 16 },
        990: { slidesPerView: 3, spaceBetween: 20 }
      }
    },

    /* Home product rails (Bestsellers, Pilates Socks, Bundles) and the
       category tiles. Used by sections/reformly-home-rail.liquid and
       sections/reformly-home-categories.liquid.

       slidesPerView: 'auto' ON PURPOSE, and this is the whole reason the
       conversion did not move a pixel. The card width is not a slide count in
       the design — it steps at 700 and 1100, which are NOT the 900 layout
       breakpoint:

         < 700    calc(100% - 64px)        one card plus a peek of the next
         700-1099 46%                      two-up
         >= 1100  calc((100% - 30px) / 4)  four-up

       Expressing that as slidesPerView means solving
       spv = (C + space) / (0.46C + space) for 46%, which is width-dependent
       and so cannot be written as a fixed number per breakpoint. With 'auto'
       the widths stay in assets/reformly-home-rail.css, already measured
       against the export, and Swiper reads them.

       spaceBetween, not a CSS gap: with 'auto' Swiper builds its snap grid
       from each slide's own width PLUS spaceBetween, and a CSS `gap` is not
       in that sum — every slide past the first would land short by the
       accumulated gap. 16 below 900 (= the page gutter) and 10 from 900,
       which is the step the stylesheet used to own.

       ONE CARD PER SWIPE ON PHONES ONLY — freeMode is off below 900 and back
       on from 900 up. Both halves are deliberate; do not collapse them.

       BELOW 900: a swipe advances exactly one product, however hard it is
       flicked. Asked for directly, twice. freeMode off is the whole change —
       with `slidesPerView: 'auto'`, `slidesPerGroup` stays 1 and Swiper snaps
       to the next slide boundary. Do not "improve" this back to momentum.

       FROM 900: freeMode + sticky, which is what it was before. Turning it off
       at every width broke the desktop rail — cards came to rest half out of
       view and then jumped on the next drag.

       The reason is in the scrollbar note further down: the rail's gutter is
       the CONTAINER'S PADDING, and with `slidesPerView: 'auto'` Swiper builds
       its snap grid from slide widths plus spaceBetween, which does not include
       that padding. So the snap positions sit a gutter's width away from where
       the cards actually are. freeMode + sticky hides it — it settles on the
       nearest slide and the small error reads as momentum — but hard snapping
       lands on those wrong positions exactly, which is the jump.

       On a phone the rail is one card wide, so the error has nowhere to
       accumulate and hard snapping is clean. Fixing it properly at desktop
       means moving the gutter off padding and onto slidesOffsetBefore/After,
       which changes what `100%` resolves against and so changes every measured
       card width — the same trade that note declines to re-open.

       mousewheel.forceToAxis is NOT optional. Before the conversion the rail
       was a native overflow-x scroller, so a trackpad two-finger swipe moved
       it for free. Swiper's container is overflow: hidden, so without this
       that gesture silently stops working on every Mac. forceToAxis keeps
       vertical wheel deltas going to the page, and releaseOnEdges hands the
       gesture back once the rail is at either end instead of swallowing it.

       ONE MEASURED DEVIATION, in the scrollbar thumb's width at mobile only.

       The design derives it as visible/total, where both come from a padded
       native scroller: at 390 that is clientWidth 390 over scrollWidth 1876
       (6 x 294 + 5 x 16 + both 16px gutters) = 20.79%, a 74.4px thumb on the
       358px track. Swiper's `dragSize: 'auto'` uses its own size/virtualSize,
       which are content-box and exclude the gutters: 358/1844 = 19.41%, so
       69.5px. 4.9px narrower, on a bar 2px tall, at one breakpoint — at 900
       and up the rail has no padding and the two agree exactly (864.4px).

       Correctable only by moving the gutter off the rail's padding and onto
       slidesOffsetBefore/After, which changes what `100%` resolves against
       and so changes every card width — 294 becomes 326 unless the calc is
       rewritten too. Not worth re-opening verified card geometry for 4.9px of
       a 2px indicator. */
    rails: {
      slidesPerView: 'auto',
      /* 8 at EVERY width — the V4 files moved the cards closer together and
         dropped the old 16/10 step. The four-up card width below depends on
         it: calc((100% - 24px) / 4) is three 8px gaps. */
      spaceBetween: 8,
      freeMode: false,
      grabCursor: true,
      watchOverflow: true,
      resistanceRatio: 0.3,
      /* DIRECTIONAL LOCK. Swiper's default `touchAngle` is 45deg, so any drag
         less than half-diagonal grabs the rail — on a phone that means a
         thumb-swipe intended to scroll the PAGE drags the carousel sideways
         instead, which is the classic way a product rail feels like it is
         fighting you. 30deg keeps deliberate horizontal swipes working and
         hands anything steeper back to the page.

         Carousel guidance (Material 3, and the Nike-style scroll-snap write
         ups) all call for a tight horizontal lock for exactly this reason;
         the commonly cited band is 10-15deg off-axis, but that is measured
         from horizontal as a REJECTION threshold. Swiper's angle is the
         half-cone it accepts, so 30 is the equivalent, and dropping it much
         further starts rejecting genuine swipes from users holding the phone
         at an angle.

         40, NOT 30. Swiper decides the axis ONCE, from the first few pixels
         of the gesture, and rejects the whole swipe if that opening vector is
         steeper than this. 30 rejected real swipes: a second swipe is thrown
         from a thumb that is already extended across the screen, so it starts
         on a noticeably steeper arc than the first — which is exactly the
         "I swipe, then re-swipe and nothing happens" case.

         40 still sits inside Swiper's 45 default, so a deliberate vertical
         scroll is handed back as before; it just stops discarding the angled
         flicks that a thumb actually produces. */
      touchAngle: 40,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true
      },
      breakpoints: {
        900: {
          freeMode: { enabled: true, sticky: true, momentumBounce: false }
        }
      }
    }
  };

  /* BACK-NAVIGATION PULLS THE PAGE TO THE RAIL. This drops the focus that
     causes it.

     Tap a card, land on the PDP, press back. The browser restores the page
     AND the focus, so the card link you tapped is `document.activeElement`
     again — sitting inside a rail, off-screen. Anything that then touches
     that element scrolls it into view, and the browser carries the page with
     it: measured here, calling focus() on a rail card moved the page from
     8696 to 564. On a phone that reads as scrolling down to the FAQs and
     being thrown back up to the rail unprompted.

     There is no way to intercept the scroll a focus causes, so the fix is to
     not be focused: on a restore, if the active element sits inside one of
     our rails, blur it. Nothing visible is lost — the focus ring was on an
     element the user cannot see.

     NOT gated on `event.persisted`. It was, and that was wrong: Shopify sends
     no-store on most pages, so a back is usually a history RELOAD rather than
     a bfcache restore and `persisted` is false — the guard skipped the exact
     case it was written for. A history navigation of either kind is the
     trigger, so that is what this tests. */
  window.addEventListener('pageshow', function (e) {
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (!e.persisted && !(nav && nav.type === 'back_forward')) return;
    var el = document.activeElement;
    if (!el || !el.closest) return;
    if (el.closest('[data-bp-swiper]') && typeof el.blur === 'function') el.blur();
  });

  /* PRESS STATE, DECIDED IN JS RATHER THAN BY `:active`.

     The problem `:active` cannot solve: it turns on the instant a finger
     lands, before anyone — including the browser — knows whether this is a
     tap or the start of a swipe. Tuning CSS around that is a race. A delay
     long enough to hide the squeeze on a flick is also long enough to hide it
     on a tap, which is exactly the trade we kept failing to split.

     Here the two cases are distinguished by what actually happens:

       moved more than SLOP      -> it was a drag. Never show, or withdraw.
       released without moving   -> it was a tap. Show it, for long enough to
                                    be seen even if the finger was quick.

     ARMED, not shown, on pointerdown. The class only lands after HOLD ms, so
     a flick cancels it before anything paints — no flicker, and no dependence
     on how fast Swiper reports the drag.

     The MIN_SHOW half is what the CSS version could not do. A tap can be
     shorter than HOLD, which previously meant no feedback at all; here, a
     release with no movement shows the press even if it has not fired yet,
     and holds it MIN_SHOW so it is visible rather than a single frame.

     pointercancel matters as much as pointermove: the browser fires it when
     it takes the gesture over for scrolling, which is the authoritative "this
     was never a tap" signal and arrives without us guessing a threshold.

     The numbers and the two edge cases below are taken from the libraries
     that already solved this, not invented:

       - UIKit's UIScrollView delays touch delivery to its content by default,
         "to help avoid undesired flashing due to content reacting to the
         touch-down event when it is quickly cancelled as the user begins a
         pan gesture". That is this mechanism, in the framework.
       - Material Web's ripple runs pointerdown -> TOUCH_DELAY_MS (150) ->
         holding, and ends only on pointerup / pointercancel / click. */
  var PRESS_SLOP = 6;       // px of movement that makes it a drag, not a tap
  var PRESS_HOLD = 150;     // ms of stillness before the press is shown
  var PRESS_MIN_SHOW = 120; // ms to keep it visible once shown

  function pressState(root) {
    var card = null, sx = 0, sy = 0, showAt = 0, holdTimer = null;

    function clear(force) {
      if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }
      if (!card) return;
      var el = card;
      card = null;
      if (!el.classList.contains('is-pressed')) return;
      var left = PRESS_MIN_SHOW - (Date.now() - showAt);
      if (force || left <= 0) el.classList.remove('is-pressed');
      else setTimeout(function () { el.classList.remove('is-pressed'); }, left);
    }

    function show() {
      holdTimer = null;
      if (!card) return;
      showAt = Date.now();
      card.classList.add('is-pressed');
    }

    root.addEventListener('pointerdown', function (e) {
      if (!e.isPrimary) return;
      /* The wishlist heart sits INSIDE the card but is not part of it. It has
         its own press state (scale 0.9, reformly-wish.css), so letting the
         card squeeze too reads as pressing the card, which is a different
         action — the card navigates, the heart saves. */
      if (e.target.closest && e.target.closest('.rwish-heart')) return;
      var el = e.target.closest ? e.target.closest('.rhm-rail-sec__card') : null;
      if (!el || !root.contains(el)) return;
      clear(true);
      card = el; sx = e.clientX; sy = e.clientY;
      holdTimer = setTimeout(show, PRESS_HOLD);
    }, { passive: true });

    root.addEventListener('pointermove', function (e) {
      if (!card) return;
      if (Math.abs(e.clientX - sx) > PRESS_SLOP || Math.abs(e.clientY - sy) > PRESS_SLOP) {
        clear(true);
      }
    }, { passive: true });

    /* A release with no movement is a tap by definition, so show the press
       even if PRESS_HOLD has not elapsed — clear() then keeps it up for the
       remainder of MIN_SHOW rather than removing it on the same frame. */
    root.addEventListener('pointerup', function () {
      if (card && holdTimer) show();
      clear(false);
    }, { passive: true });

    root.addEventListener('pointercancel', function () { clear(true); }, { passive: true });

    /* pointerleave is handled for MOUSE ONLY, deliberately.

       Material Web shipped a fix for exactly this: "ends a touch press only on
       a pointerup, pointercancel or click delivered to the host, and it
       IGNORES a touch pointerleave". During a touch drag the pointer leaves
       and re-enters the host constantly, so treating leave as an end makes
       the state flicker. A mouse leaving the card really has ended the press. */
    root.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'touch') clear(true);
    }, { passive: true });

    /* RELEASE OUTSIDE THE RAIL. The listeners above are on the rail, so a
       finger that lifts beyond it never delivers pointerup here and the card
       stays pressed — the "ripple holds at pressed opacity indefinitely" bug
       Material hit. Window-level cleanup closes it; it is a no-op whenever the
       rail's own handler already ran, because clear() exits on a null card. */
    ['pointerup', 'pointercancel'].forEach(function (type) {
      window.addEventListener(type, function () { clear(true); }, { passive: true });
    });
  }

  /* Returns TRUE only when the rail is actually initialised. The caller needs
     to know: lazyBoot() must not stop watching a rail it failed to build. */
  function build(root) {
    if (root.dataset.bpSwiperReady === '1') return true;
    if (typeof window.Swiper !== 'function') return false;

    var preset = PRESETS[root.dataset.bpSwiper] || {};
    var extra = {};
    if (root.dataset.bpOpts) {
      try {
        extra = JSON.parse(root.dataset.bpOpts);
      } catch (e) {
        console.warn('[bp] bad data-bp-opts on', root, e);
      }
    }

    var opts = {};
    var k;
    for (k in preset) opts[k] = preset[k];
    for (k in extra) opts[k] = extra[k];

    /* TELL CSS WHEN A DRAG IS IN PROGRESS, so a swipe stops reading as a press.

       `:active` fires the instant a finger lands, and on a rail that is also
       the instant a swipe begins — CSS cannot tell a tap from a pan at that
       moment because neither can the browser. Dragging through the cards
       therefore squeezed each one in turn.

       Native apps solve it the same way: show the highlight, then CANCEL it
       once the gesture turns out to be a scroll. `sliderMove` is Swiper's
       "this is really a drag now" signal — it fires only after the touch has
       moved past the threshold and cleared touchAngle, so a straight tap
       never reaches it.

       The remaining gap, between touchdown and the first move, is covered in
       CSS by a 60ms transition-delay on the pressed state: a flick flips this
       class before the squeeze has started animating, so it never paints.

       Applied in build() rather than in the `rails` preset so every Swiper on
       the site gets it, and merged with any handlers a preset or data-bp-opts
       already defines rather than replacing them. */
    var userOn = opts.on || {};
    opts.on = {};
    for (k in userOn) opts.on[k] = userOn[k];

    var userMove = opts.on.sliderMove;
    var userEnd = opts.on.touchEnd;

    opts.on.sliderMove = function (sw, e) {
      if (sw.el) sw.el.classList.add('is-dragging');
      if (userMove) userMove.call(this, sw, e);
    };

    opts.on.touchEnd = function (sw, e) {
      if (sw.el) sw.el.classList.remove('is-dragging');
      if (userEnd) userEnd.call(this, sw, e);
    };

    // Wire nav/pagination only when the section actually rendered them,
    // otherwise Swiper logs about missing elements on every init.
    var wrap = root.closest('[data-bp-swiper-wrap]') || root.parentNode;
    var prev = wrap.querySelector('[data-bp-prev]');
    var nextEl = wrap.querySelector('[data-bp-next]');
    var pag = wrap.querySelector('[data-bp-pagination]');

    if (prev && nextEl) opts.navigation = { prevEl: prev, nextEl: nextEl };
    if (pag) opts.pagination = { el: pag, clickable: true };

    /* Scrollbar, wired the same way — opt in with [data-bp-scrollbar] on the
       track element.

       `dragSize: 'auto'` is Swiper's own `trackSize * (size / virtualSize)`,
       which is the identical formula the home rails' design uses for their
       2px bar, so the thumb comes out the same width it did when a hand-
       written script painted it.

       The track may already contain its own thumb element: Swiper reuses a
       child carrying `dragClass` rather than appending a second one, so a
       section can keep a styled thumb of its own by putting
       `swiper-scrollbar-drag` on it. That is how the home rails keep their
       purple gradient. */
    var sb = wrap.querySelector('[data-bp-scrollbar]');
    if (sb) opts.scrollbar = { el: sb, draggable: true, dragSize: 'auto' };

    opts.a11y = { enabled: true };
    // Free-scroll feel on touch without snapping mid-card.
    if (opts.slidesPerView && String(opts.slidesPerView).indexOf('.') > -1) {
      opts.watchOverflow = true;
    }

    /* No observer/observeParents — see the note in reformly-bp-hero.js.

       Briefly: they were a guess at a sizing bug whose real cause was CSS, and
       observeParents watches the whole ancestor chain for mutations, running a
       full slide re-measure on each one. With lazysizes stamping classes onto
       images during scroll, that became continuous work on this page. */
    root.dataset.bpSwiperReady = '1';
    var instance = new window.Swiper(root, opts);

    pressState(root);

    /* Announce the instance so a section can decorate it without owning the
       init. The home rails need this: their progress bar used to read the
       rail's scrollLeft, and after the conversion there is no scroll to read
       — the position lives on the Swiper. They cannot simply look at
       `el.swiper` on load either, because boot() is deferred by the
       IntersectionObserver below, so the instance does not exist yet when
       their script runs.

       Bubbling, so one document-level listener covers every rail including
       sections the theme editor adds later. */
    root.dispatchEvent(new CustomEvent('bp:swiper', {
      bubbles: true,
      detail: { swiper: instance, preset: root.dataset.bpSwiper }
    }));

    return true;
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-bp-swiper]');
    for (var i = 0; i < els.length; i++) build(els[i]);
  }

  /* Exposed for carousels whose markup is REPLACED while the page lives, which
     the lazyBoot observer below cannot see: it observes the nodes present at
     load, and a node swapped in later was never registered.

     The cart drawer is the case. liquid-ajax-cart re-renders it on every cart
     change, so its "Looking for more?" rail arrives as a fresh element with
     no data-bp-swiper-ready flag and no observer watching it. It calls this
     after each render and on open, when the panel first has a width to
     measure.

     Safe to call repeatedly — build() returns early on an element it has
     already initialised. */
  window.bpSwiperBoot = boot;

  /* Carousels below the fold do not need Swiper booted at load. An
     observer defers each one until it is close to the viewport, which
     keeps the main thread free for the hero. */
  function lazyBoot() {
    var els = document.querySelectorAll('[data-bp-swiper]');
    if (!('IntersectionObserver' in window)) return boot();

    /* UNOBSERVE ONLY ON SUCCESS. This used to unobserve unconditionally, which
       left the FIRST rail on the page dead.

       swiper-bundle.min.js is deferred, so `window.Swiper` does not exist for
       the first moments after DOMContentLoaded. Any rail already within the
       300px margin at that instant — i.e. the top one, Bestsellers on the home
       page — intersects straight away, build() bails because Swiper is not
       there yet, and the old code stopped watching it regardless. It then
       never got another chance: the rail stayed a plain flex row inside an
       `overflow: hidden` viewport, so its cards ran past the right edge and
       the last one was sliced in half.

       Rails further down the page were fine, because by the time they scrolled
       into range the bundle had loaded — which is why this looked like a
       problem with one specific rail rather than a race. */
    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting && build(entries[i].target)) {
            io.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '300px 0px' }
    );
    for (var i = 0; i < els.length; i++) io.observe(els[i]);

    /* Backstop for the same race: a rail sitting in view at load gets exactly
       one intersection callback and no further ones until the user scrolls, so
       without this it would stay unbuilt even though the observer is still
       watching. `load` fires after the deferred bundle has run. build() is
       idempotent via the data flag, so re-running costs nothing. */
    window.addEventListener('load', function () { boot(); }, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', lazyBoot);
  } else {
    lazyBoot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
  });
})();

/* ------------------------------------------------------------
   Click-to-play video posters. Shared by every section that shows
   a poster over a <video>: nothing autoplays, nothing preloads, so
   a page with 10 clips still costs zero video bytes until asked.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bp-play]');
    if (!btn) return;

    var holder = btn.closest('[data-bp-video]');
    if (!holder) return;

    var video = holder.querySelector('video');
    if (!video) return;

    // Pause anything else already running — two clips talking over each
    // other is the usual bug when every card owns its own handler.
    var playing = document.querySelectorAll('[data-bp-video].is-playing video');
    for (var i = 0; i < playing.length; i++) {
      if (playing[i] !== video) {
        playing[i].pause();
        playing[i].closest('[data-bp-video]').classList.remove('is-playing');
      }
    }

    holder.classList.add('is-playing');
    var p = video.play();
    if (p && p.catch) p.catch(function () { holder.classList.remove('is-playing'); });
  });

  /* Stop button. Reverting .is-playing is all the UI needs — the same class
     drives the video's opacity, the poster and the play button — so this only
     has to pause and remove it.

     currentTime reset so the card goes back to its resting state rather than
     re-opening on a frozen mid-clip frame the next time it is played. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-bp-close]');
    if (!btn) return;

    var holder = btn.closest('[data-bp-video]');
    if (!holder) return;

    var video = holder.querySelector('video');
    if (video) {
      video.pause();
      try { video.currentTime = 0; } catch (err) { /* not seekable yet */ }
    }
    holder.classList.remove('is-playing');
  });

  /* Scrolling away pauses it.

     The click handler above only stops a clip when ANOTHER one starts, so a
     video kept playing once its card left the screen — the rail snaps to the
     next card and you still hear the last one. Same on plain page scroll.

     An observer rather than a scroll listener on the rail: this has to cover
     the horizontal snap rail, vertical page scroll, and the hero's video
     rail, and all three are just "is it still on screen". Half visible is the
     threshold, so a card that is merely clipped at the edge keeps playing.

     Reverting .is-playing also brings the poster and play button back, so the
     card returns to its resting state rather than showing a frozen frame. */
  /* Strip the native buttons we do not want, leaving the transport and the
     sound control.

     Set in JS rather than on the tag because these clips render through two
     paths — Shopify's `video_tag` filter and a hand-written <video> for raw
     Files URLs — and the filter does not pass arbitrary attributes through.

     What each one removes:
       x-webkit-airplay="deny"      the AirPlay glyph (Safari/iOS only)
       disablePictureInPicture      the PiP glyph
       controlsList                 download, playback-rate and cast buttons
     iOS keeps its fullscreen button regardless; there is no attribute for it,
     which is why the close button moved to the top-left instead. */
  function trimControls(scope) {
    var vids = (scope || document).querySelectorAll('[data-bp-video] video');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      v.setAttribute('x-webkit-airplay', 'deny');
      v.setAttribute('disablePictureInPicture', '');
      v.setAttribute('controlsList', 'nodownload noplaybackrate noremoteplayback');
    }
  }

  function watch(scope) {
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var en = entries[i];
        if (en.isIntersecting) continue;
        var holder = en.target;
        if (!holder.classList.contains('is-playing')) continue;
        var v = holder.querySelector('video');
        if (v) v.pause();
        holder.classList.remove('is-playing');
      }
    }, { threshold: 0.5 });

    var els = (scope || document).querySelectorAll('[data-bp-video]');
    for (var i = 0; i < els.length; i++) io.observe(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { watch(); trimControls(); });
  } else {
    watch();
    trimControls();
  }

  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', function (e) {
    watch(e.target);
    trimControls(e.target);
  });
})();

/* ------------------------------------------------------------
   Snap-rail pagination dots.

   The proof rail is a plain CSS scroll-snap container, not a Swiper, so
   nothing reports its position. This keeps the dots in step with it and
   lets a dot scroll the rail, matching the design: 6px dots that stretch
   to 22px for the current card.

   Scroll is read through rAF rather than on every scroll event, and the
   active index comes from comparing offsetLeft to scrollLeft so it stays
   correct regardless of card width or gap.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  function init(dots) {
    if (dots.dataset.rbpDotsReady === '1') return;
    /* Rail selector is overridable: the review rail is .rbp-cards, the video
       testimonials rail is .rbp-vt. Hardcoding the former meant the dots
       markup silently did nothing anywhere else. */
    var rail = dots.parentNode.querySelector(dots.getAttribute('data-rbp-dots-rail') || '.rbp-cards');
    if (!rail) return;
    dots.dataset.rbpDotsReady = '1';

    var buttons = dots.querySelectorAll('[data-rbp-dot]');

    function current() {
      var best = 0, bestGap = Infinity;
      for (var i = 0; i < rail.children.length; i++) {
        var gap = Math.abs(rail.children[i].offsetLeft - rail.scrollLeft);
        if (gap < bestGap) { bestGap = gap; best = i; }
      }
      return best;
    }

    function paint() {
      var n = current();
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].setAttribute('aria-current', i === n ? 'true' : 'false');
      }
    }

    var queued = false;
    rail.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; paint(); });
    }, { passive: true });

    for (var i = 0; i < buttons.length; i++) {
      (function (idx) {
        buttons[idx].addEventListener('click', function () {
          var card = rail.children[idx];
          if (card) rail.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
        });
      })(i);
    }

    paint();
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-rbp-dots]');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
